import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faStar, faGem, faHistory, faUsers, faAward } from '@fortawesome/free-solid-svg-icons';
import './About.css';

function About() {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col lg={6}>
              <div className="about-hero-content">
                <h1 className="about-title">About My Collection Journey</h1>
                <p className="about-subtitle">
                  Welcome to my personal universe of collectibles, where every piece 
                  tells a story and every collection holds precious memories.
                </p>
              </div>
            </Col>
            <Col lg={6}>
              <div className="about-hero-image">
                <img 
                  src="/images/room_wide.jpg" 
                  alt="Collection room" 
                  className="img-fluid rounded-lg shadow-lg"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Story Section */}
      <section className="about-story">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <div className="story-content">
                <h2 className="section-title">My Story</h2>
                <p className="story-text">
                  What started as a childhood fascination with Star Wars has evolved into 
                  a passionate journey through the world of collectibles. From vintage action 
                  figures to rare Pokemon cards, each piece in my collection represents a 
                  moment in time, a memory, or a dream fulfilled.
                </p>
                <p className="story-text">
                  This digital showcase isn't just about displaying items—it's about 
                  sharing the joy, the hunt for rare pieces, and the stories behind 
                  each treasure. Whether it's a mint-condition Darth Vader figure or 
                  a holographic Charizard card, every collectible has earned its place 
                  through careful curation and genuine appreciation.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="about-features">
        <Container>
          <Row>
            <Col lg={12}>
              <h2 className="section-title text-center">What Makes This Collection Special</h2>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}>
              <Card className="feature-card h-100">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faGem} className="feature-icon" />
                  <h4>Premium Quality</h4>
                  <p>Every item is carefully selected for its condition, rarity, and authenticity.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="feature-card h-100">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faHistory} className="feature-icon" />
                  <h4>Rich History</h4>
                  <p>Each piece comes with its story, from acquisition to its place in pop culture.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="feature-card h-100">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faHeart} className="feature-icon" />
                  <h4>Personal Touch</h4>
                  <p>More than just collecting—it's about preserving memories and sharing passion.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="feature-card h-100">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faStar} className="feature-icon" />
                  <h4>Rare Finds</h4>
                  <p>Discover unique and hard-to-find pieces that any collector would treasure.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="feature-card h-100">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faUsers} className="feature-icon" />
                  <h4>Community</h4>
                  <p>Connecting with fellow collectors and sharing the joy of discovery.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="feature-card h-100">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faAward} className="feature-icon" />
                  <h4>Expertise</h4>
                  <p>Years of collecting experience ensuring authenticity and value.</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Collections Overview */}
      <section className="collections-overview">
        <Container>
          <Row>
            <Col lg={12}>
              <h2 className="section-title text-center">My Collections</h2>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={3}>
              <div className="collection-preview">
                <div className="collection-image">
                  <img src="/images/1.jpg" alt="Star Wars Collection" className="img-fluid" />
                </div>
                <h4>Star Wars</h4>
                <p>Vintage figures, rare variants, and iconic characters from a galaxy far, far away.</p>
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="collection-preview">
                <div className="collection-image">
                  <img src="/images/2.jpg" alt="Pokemon Collection" className="img-fluid" />
                </div>
                <h4>Pokemon Cards</h4>
                <p>From Base Set classics to modern rarities, gotta catch 'em all!</p>
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="collection-preview">
                <div className="collection-image">
                  <img src="/images/funko.jpg" alt="Funko Collection" className="img-fluid" />
                </div>
                <h4>Funko Pops</h4>
                <p>Exclusive releases, convention specials, and beloved character variants.</p>
              </div>
            </Col>
            <Col md={6} lg={3}>
              <div className="collection-preview">
                <div className="collection-image">
                  <img src="/images/handhelds.jpg" alt="Vintage Collection" className="img-fluid" />
                </div>
                <h4>Vintage Items</h4>
                <p>Pocket watches, vintage games, and timeless treasures from bygone eras.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="about-cta">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h2>Ready to Explore?</h2>
              <p className="lead">
                Dive into my collections and discover the stories behind each treasure. 
                Whether you're a fellow collector or simply curious, there's something 
                here for everyone to enjoy.
              </p>
              <div className="cta-buttons">
                <a href="/" className="btn btn-primary btn-lg me-3">
                  <FontAwesomeIcon icon={faGem} className="me-2" />
                  View Collections
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

export default About;
